
import BackButton from "@/components/ui/Buttons/BackButton.vue";
import UserBalance from "@/components/User/UserBalance.vue";
import AccountStatus from "@/components/User/AccountStatus.vue";
import AppCollapsable from "@/components/ui/Collapsable/AppCollapsable.vue";
import NumberUsageModal from "@/components/User/VirtualNumbers/NumberUsageModal/NumberUsageModal.vue";
import DeleteUserDialog from "@/components/ui/Modal/Dialog/instances/DeleteUserDialog.vue";
import AccountInfo from "@/components/User/AccountInfo.vue";
import { mapGetters, useStore } from "vuex";
import { useRoute } from "vue-router";
import { computed, watch, defineComponent } from "vue";
import { useAccount } from "@/hooks/admin/useAdmin";
import { useBalances } from "@/hooks/balances/useBalances";
import { usePayments } from "@/hooks/payments/usePayments";
import FullscreenLoader from "@/components/ui/FullscreenLoader.vue";
import BundlesList from "@/components/User/BundlesList.vue";
import EsimList from "@/components/User/EsimList.vue";

interface UserEditData {
  email: string;
  fullName: string;
  id: number;
}

export default defineComponent({
  name: "User",
  components: {
    EsimList,
    FullscreenLoader,
    AccountInfo,
    DeleteUserDialog,
    NumberUsageModal,
    AppCollapsable,
    AccountStatus,
    BackButton,
    UserBalance
  },
  provide() {
    return {
      userId: this.userId,
      openUsageLogsModal: this.showUsageLogsModal
    };
  },
  setup() {
    const store = useStore();
    const router = useRoute();

    const viewConfiguration: { page: number; perPage: number } = {
      page: 1,
      perPage: 1000
    };
    const userId = Number(router.query.userId);
    const options = { viewConfiguration, userId };
    const paymentViewConfig = {
      paymentType: "All",
      status: "All",
      date: "",
      page: 1,
      perPage: 10
    };
    const filter = {
      type: "",
      from: "",
      to: ""
    };
    const paymentOptions = {
      viewConfig: paymentViewConfig,
      filter
    };
    const {
      getUserInfo,
      getLoginHistory,
      removeUser,
      uploadAvatar,
      editUser,
      getPaymentHistoryById
    } = useAccount(options);
    const {
      getUserBalance
    } = useBalances(userId)
    const { groupByDate, formatDate } = usePayments(paymentOptions);
    const notesOptions = {
      query: {
        hold: false,
        take: 10,
        skip: 0
      },
      id: userId
    };

    // Watch routers
    watch(router, route => {
      let { path } = route;
      if (path === "/user") {
        getUserInfo();
      }
    });
    // Call functions
    store.dispatch("setAppLoading", true);
    Promise.all([
      getUserInfo(), // fetch accounts on page created
      getUserBalance()
    ]).then(() => {
      store.dispatch("setAppLoading", false);
    });

    return {
      userInformation: computed(() => store.getters.userInformation),
      userBalance: computed(() => store.getters.userBalance),
      getUserInfo,
      removeUser,
      uploadAvatar,
      editUser,
      formatDate,
      userId,
      groupByDate,
      paymentViewConfig
    };
  },
  data: () => {
    return {
      name: "Sophie Turner",
      usageLogsActiveModalId: null as null | number,
      deleteUserDialogShown: false,
      isLoading: false,
      useLoader: true,
      editData: {
        email: "",
        fullName: ""
      } as UserEditData
    };
  },
  computed: {
    ...mapGetters(["appLoading"]),
    accountInfo() {
      return this.userInformation;
    },
    userName() {
      return (
        this.userInformation?.["firstName"] +
        " " +
        this.userInformation?.["lastName"]
      );
    },
    emailVerified(): boolean | null | undefined {
      return this.userInformation?.["emailVerified"];
    },
    blocked(): boolean | null | undefined {
      return this.userInformation?.["blocked"];
    }
  },
  watch: {
    userInformation: {
      handler(data) {
        this.editData.email = data.email;
        this.editData.fullName = data.fullName;
        this.editData.id = this.userId;
      },
      deep: true
    }
  },
  methods: {
    showUsageLogsModal(id: string | undefined) {
      if (typeof id === "string") {
        this.usageLogsActiveModalId = this.userId;
      } else {
        throw new Error(`${id} - is not valid number id`);
      }
    },
    cleanData() {
      this.deleteUserDialogShown = false;
    },
    hideUsageLogsModal() {
      this.usageLogsActiveModalId = null;
    },

    onUserDelete() {
      this.deleteUserDialogShown = true;
    },
    async removeUserHandler() {
      this.isLoading = true;
      const success = await this.removeUser();
      if (success) {
        this.cleanData();
        this.$router.back();
      }
      this.isLoading = false;
    }
  }
});
